import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Grid,
  Paper,
  TextField,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../DataLayouts/Header";
import toast from "react-hot-toast";
import useAuthStore from "../../../Zustand/Store/useAuthStore";
import {
  createOrganizationLevel,
  fetchOrganizationLevelById,
  updateOrganizationLevel,
} from "../../../Apis/OrganizationLevel-api";

function OrganizationLevelForm({ mode }) {
  const { id } = useParams();
  const { userData } = useAuthStore();
  const org = userData?.organization;

  const [formData, setFormData] = useState({
    level_name: "",
    level_code: "",
    level_order: "",
    description: "",
    notice_period_days: "",
    is_active: true,
  });

  const [formErrors, setFormErrors] = useState({});
  const [loading, setLoading] = useState(mode === "edit" || mode === "view");
  const [btnLoading, setbtnLoading] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const getDataById = async () => {
      try {
        const level = await fetchOrganizationLevelById(
          org.organization_id,
          id
        );
        setFormData({
          level_name: level?.level_name || "",
          level_code: level?.level_code || "",
          level_order: level?.level_order ?? "",
          description: level?.description || "",
          notice_period_days: level?.notice_period_days ?? "",
          is_active: Boolean(level?.is_active),
        });
      } catch (error) {
        toast.error("Failed to load organization level");
      } finally {
        setLoading(false);
      }
    };

    if ((mode === "edit" || mode === "view") && id) {
      setLoading(true);
      getDataById();
    }
  }, [mode, id, org?.organization_id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.level_name?.trim()) {
      errors.level_name = "Level name is required.";
    }

    if (!formData.level_order || Number(formData.level_order) < 1) {
      errors.level_order = "Level order must be at least 1.";
    }

    if (
      formData.notice_period_days !== "" &&
      Number(formData.notice_period_days) < 0
    ) {
      errors.notice_period_days = "Notice period cannot be negative.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const buildPayload = () => ({
    level_name: formData.level_name.trim(),
    level_code: formData.level_code?.trim() || null,
    level_order: Number(formData.level_order),
    description: formData.description?.trim() || null,
    notice_period_days:
      formData.notice_period_days === ""
        ? null
        : Number(formData.notice_period_days),
    is_active: Boolean(formData.is_active),
  });

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setbtnLoading(true);

    try {
      const payload = buildPayload();
      if (mode === "edit") {
        await updateOrganizationLevel(org.organization_id, id, payload);
      } else {
        await createOrganizationLevel(org.organization_id, payload);
      }

      toast.success(
        mode === "edit"
          ? "Organization level updated!"
          : "Organization level created!"
      );
      setFormErrors({});
      navigate(-1);
    } catch (err) {
      if (err.response?.status === 422) {
        const validationErrors = err.response.data.errors || {};
        setFormErrors(validationErrors);
        const errorMessages = Object.values(validationErrors)
          .map((arr) => arr[0])
          .join(" ");
        toast.error(errorMessages || "Validation failed.");
      } else {
        toast.error(
          err.response?.data?.message || "Something went wrong."
        );
      }
    } finally {
      setbtnLoading(false);
    }
  };

  return (
    <Header
      mode={mode}
      updateMessage={"Organization Level"}
      addMessage={"Organization Level"}
      homeLink={"/organization-configration/organization-level"}
      homeText={"Organization Levels"}
      onSubmit={handleSubmit}
      loading={loading}
      btnLoading={btnLoading}
    >
      {loading ? (
        <Grid container spacing={2}>
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        </Grid>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12} md={8}>
            <Paper elevation={4} sx={{ p: 3 }}>
              <Grid container spacing={2}>
                <TextField
                  fullWidth
                  label="Level Name"
                  name="level_name"
                  value={formData.level_name}
                  onChange={handleChange}
                  disabled={mode === "view"}
                  error={!!formErrors.level_name}
                  helperText={formErrors.level_name}
                  required
                  inputProps={{ maxLength: 100 }}
                />

                <TextField
                  fullWidth
                  label="Level Code"
                  name="level_code"
                  value={formData.level_code}
                  onChange={handleChange}
                  disabled={mode === "view"}
                  error={!!formErrors.level_code}
                  helperText={formErrors.level_code}
                  inputProps={{ maxLength: 50 }}
                />

                <TextField
                  fullWidth
                  label="Level Order"
                  name="level_order"
                  type="number"
                  value={formData.level_order}
                  onChange={handleChange}
                  disabled={mode === "view"}
                  error={!!formErrors.level_order}
                  helperText={formErrors.level_order}
                  required
                  inputProps={{ min: 1 }}
                />

                <TextField
                  fullWidth
                  label="Notice Period (days)"
                  name="notice_period_days"
                  type="number"
                  value={formData.notice_period_days}
                  onChange={handleChange}
                  disabled={mode === "view"}
                  error={!!formErrors.notice_period_days}
                  helperText={formErrors.notice_period_days}
                  inputProps={{ min: 0 }}
                />

                <TextField
                  fullWidth
                  label="Description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  disabled={mode === "view"}
                  error={!!formErrors.description}
                  helperText={formErrors.description}
                  multiline
                  minRows={2}
                  inputProps={{ maxLength: 255 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(formData.is_active)}
                      onChange={handleChange}
                      name="is_active"
                      disabled={mode === "view"}
                    />
                  }
                  label="Active"
                />
              </Grid>

              <Grid container spacing={2} mt={2}>
                <Grid item>
                  <Button
                    variant="contained"
                    color="primary"
                    size="medium"
                    onClick={handleSubmit}
                    disabled={loading || btnLoading || mode === "view"}
                    sx={{
                      borderRadius: 2,
                      minWidth: 120,
                      textTransform: "capitalize",
                      fontWeight: 500,
                    }}
                  >
                    {loading || btnLoading ? (
                      <CircularProgress size={22} sx={{ color: "#fff" }} />
                    ) : (
                      "Submit"
                    )}
                  </Button>
                </Grid>

                {mode === "edit" && (
                  <Grid item>
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={() => navigate(-1)}
                      sx={{
                        borderRadius: 2,
                        minWidth: 120,
                        textTransform: "capitalize",
                        fontWeight: 500,
                        backgroundColor: "#1976d2",
                        "&:hover": { backgroundColor: "#115293" },
                      }}
                    >
                      Cancel
                    </Button>
                  </Grid>
                )}
              </Grid>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Header>
  );
}

export default OrganizationLevelForm;
